/**
 * SOAP置換処理のヘルパー関数
 *
 * @description
 * ADR-055 対応: Selection 型（Set<number>）を使用するよう修正
 */

import {
  SoapSectionKeys,
  SoapContentItem,
  SoapContent,
  Selection,
} from '@/shared/types/yakurekiSoap'
import {
  ReplacementResult,
  SectionReplacementResult,
} from '@/features/formatter/types/soapReplacement'
import { escapeRegExp } from '@/shared/utils/regexUtils'

/**
 * 単一セクションの置換処理
 * @param sectionItems セクションのアイテム配列
 * @param placeholder 置換対象のプレースホルダー
 * @param replacement 置換後の文字列
 * @param selectedIndices 現在選択されているインデックスのSet
 * @returns 置換結果
 */
export const replaceSectionItems = (
  sectionItems: SoapContentItem[],
  placeholder: string,
  replacement: string,
  selectedIndices: Set<number>
): SectionReplacementResult => {
  const escapedPlaceholder = escapeRegExp(placeholder)
  const exactRegex = new RegExp(escapedPlaceholder, 'g')
  const updatedIndices: number[] = []

  const updatedItems = sectionItems.map((item, idx) => {
    if (item.content.includes(placeholder)) {
      updatedIndices.push(idx)
      return {
        ...item,
        content: item.content.replace(exactRegex, replacement),
        is_selected: true, // 薬品を更新したアイテムは選択状態にする
      }
    }
    // 更新されていない項目は既存の選択状態を維持
    return {
      ...item,
      is_selected: selectedIndices.has(idx) ?? item.is_selected,
    }
  })

  return {
    updatedItems,
    updatedIndices,
  }
}

/**
 * 全セクション横断の置換処理
 *
 * @description
 * ADR-055 対応: Selection 型（Set<number>）を使用
 *
 * @param soapContent SOAPコンテンツ
 * @param placeholder 置換対象のプレースホルダー
 * @param replacement 置換後の文字列
 * @param selection 各セクションの選択状態（インデックスの Set）
 * @returns 置換結果
 */
export const performAllSectionsReplacement = (
  soapContent: Partial<SoapContent>,
  placeholder: string,
  replacement: string,
  selection: Selection
): ReplacementResult => {
  const updatedSoaps: { [key in SoapSectionKeys]?: SoapContentItem[] } = {}
  const updatedIndicesBySection: { [key in SoapSectionKeys]?: number[] } = {}

  Object.keys(soapContent).forEach((key) => {
    const typedKey = key as SoapSectionKeys
    const sectionItems = soapContent[typedKey]
    if (!sectionItems || !Array.isArray(sectionItems)) return

    // 現在の選択状態を取得（新方式: Set<number>）
    const selectedIndices = selection[typedKey] || new Set<number>()

    // セクションごとに置換処理を実行
    const result = replaceSectionItems(
      sectionItems,
      placeholder,
      replacement,
      selectedIndices
    )

    updatedSoaps[typedKey] = result.updatedItems
    if (result.updatedIndices.length > 0) {
      updatedIndicesBySection[typedKey] = result.updatedIndices
    }
  })

  return {
    updatedSoaps,
    updatedIndicesBySection,
  }
}

/**
 * 単一アイテムの置換処理
 * @param targetSoaps 対象セクションのアイテム配列
 * @param placeholder 置換対象のプレースホルダー
 * @param replacement 置換後の文字列
 * @param clickedIndex クリックされたアイテムのインデックス
 * @param selectedIndices 現在選択されているインデックスのSet
 * @returns 置換結果
 */
export const performSingleItemReplacement = (
  targetSoaps: SoapContentItem[],
  placeholder: string,
  replacement: string,
  clickedIndex: number,
  selectedIndices: Set<number>
): SectionReplacementResult => {
  const updatedItems = [...targetSoaps]
  const updatedIndices: number[] = []

  const newSoapContent = targetSoaps[clickedIndex].content.replace(
    placeholder,
    replacement
  )

  updatedItems[clickedIndex] = {
    ...updatedItems[clickedIndex],
    content: newSoapContent,
    is_selected: true, // 薬品を更新したアイテムは選択状態にする
  }
  updatedIndices.push(clickedIndex)

  // 他のアイテムの選択状態を維持
  const resultItems = updatedItems.map((item, idx) => ({
    ...item,
    is_selected:
      idx === clickedIndex
        ? true
        : selectedIndices.has(idx) ?? item.is_selected,
  }))

  return {
    updatedItems: resultItems,
    updatedIndices,
  }
}
